<script setup>
import { ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BusinessPresetSelector from './BusinessPresetSelector.vue';

// For an assistant that predates rubro tracking, or was built from scratch: lets the admin
// pick which rubro it belongs to, then runs the same sync a linked assistant uses to update
// from its rubro. That single call both links it (the sync writes business_preset_id back
// onto the assistant) and brings its scenarios up to date in one step.
const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['linked']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const selectedPreset = ref(null);
const isLinking = ref(false);

const handleSelect = preset => {
  selectedPreset.value = preset;
};

const handleBack = () => {
  selectedPreset.value = null;
};

const handleConfirm = async () => {
  isLinking.value = true;
  try {
    const result = await store.dispatch('captainAssistants/syncBusinessPreset', {
      presetId: selectedPreset.value.preset_id,
      assistantId: props.assistantId,
    });
    useAlert(
      t('CAPTAIN.ASSISTANTS.SCENARIOS.LINK_PRESET.SUCCESS_MESSAGE', {
        created: result?.created?.length ?? 0,
        updated: result?.updated?.length ?? 0,
      })
    );
    emit('linked');
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.message || t('CAPTAIN.ASSISTANTS.SCENARIOS.LINK_PRESET.ERROR_MESSAGE')
    );
  } finally {
    isLinking.value = false;
  }
};

const handleClose = () => {
  selectedPreset.value = null;
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :title="
      selectedPreset
        ? t('CAPTAIN.ASSISTANTS.SCENARIOS.LINK_PRESET.CONFIRM_TITLE')
        : t('CAPTAIN.ASSISTANTS.SCENARIOS.LINK_PRESET.SELECT_TITLE')
    "
    :description="
      selectedPreset
        ? t('CAPTAIN.ASSISTANTS.SCENARIOS.LINK_PRESET.CONFIRM_DESCRIPTION')
        : t('CAPTAIN.ASSISTANTS.SCENARIOS.LINK_PRESET.SELECT_DESCRIPTION')
    "
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="handleClose"
  >
    <BusinessPresetSelector
      v-if="!selectedPreset"
      :show-scratch="false"
      @select="handleSelect"
    />

    <div v-else class="flex flex-col gap-4">
      <div class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        <span
          class="text-base"
          :class="
            {
              package: 'i-lucide-package',
              'graduation-cap': 'i-lucide-graduation-cap',
              briefcase: 'i-lucide-briefcase',
              store: 'i-lucide-store',
              stethoscope: 'i-lucide-stethoscope',
            }[selectedPreset.icon] || 'i-lucide-briefcase'
          "
        />
        {{ selectedPreset.title }}
      </div>

      <div class="flex flex-col gap-1 text-sm">
        <span class="font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.SYNC_PRESET.KEEPS_TITLE') }}
        </span>
        <span class="text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.SYNC_PRESET.KEEPS_BODY') }}
        </span>
      </div>
      <div class="flex flex-col gap-1 text-sm">
        <span class="font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.LINK_PRESET.ADDS_TITLE') }}
        </span>
        <span class="text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.LINK_PRESET.ADDS_BODY') }}
        </span>
      </div>

      <div class="flex items-center justify-between gap-3 pt-2">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('CAPTAIN.ASSISTANTS.PRESET.BACK')"
          :disabled="isLinking"
          @click="handleBack"
        />
        <Button
          type="button"
          color="blue"
          :label="t('CAPTAIN.ASSISTANTS.SCENARIOS.LINK_PRESET.CONFIRM')"
          :is-loading="isLinking"
          :disabled="isLinking"
          @click="handleConfirm"
        />
      </div>
    </div>
  </Dialog>
</template>
